import { useEffect, useState } from 'react'
import { createClient, getUserApiKey } from '@/lib/supabase-client'
import Sidebar from './Sidebar'
import DashboardHeader from './DashboardHeader'

interface CategoryScore {
  score: number
  issues: string[]
  positives?: string[]
}

interface SiteAuditCategories {
  performance: CategoryScore
  accessibility: CategoryScore
  best_practices: CategoryScore
  seo: CategoryScore
}

interface SeoAuditResult {
  audit_id: string
  url: string
  requested_url: string
  overall_score: number
  categories: SiteAuditCategories
  issues: string[]
  highlights?: string[]
  recommendations?: string[]
  metrics: any
  performance: {
    load_time_ms: number | null
    dom_content_loaded_ms: number | null
    time_to_first_byte_ms: number | null
  }
  screenshot_base64?: string | null
  created_at: string
}

interface AuditHistoryItem {
  id: string
  url: string
  requested_url: string | null
  score: number
  created_at: string
}

export default function SeoAuditPage() {
  const supabase = createClient()
  const [url, setUrl] = useState('')
  const [includeScreenshot, setIncludeScreenshot] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<SeoAuditResult | null>(null)
  const [history, setHistory] = useState<AuditHistoryItem[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState<string | null>(null)

  const getApiKey = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('Please sign in to use the SEO audit tool')
    }

    const apiKey = await getUserApiKey(supabase, session.user.id)

    if (!apiKey) {
      throw new Error('Unable to retrieve API key. Please try refreshing the page.')
    }

    return apiKey
  }

  const loadHistory = async () => {
    setHistoryError(null)
    setHistoryLoading(true)
    try {
      const apiKey = await getApiKey()

      const { data, error } = await supabase
        .from('site_audits')
        .select('id, url, requested_url, score, created_at')
        .eq('api_key', apiKey)
        .order('created_at', { ascending: false })
        .limit(10)

      if (error) {
        console.error('Error loading audit history', error)
        setHistoryError('Unable to load your recent audits at the moment.')
        setHistory([])
      } else {
        setHistory(
          (data || []).map((row: any) => ({
            id: row.id,
            url: row.url,
            requested_url: row.requested_url ?? null,
            score: row.score,
            created_at: row.created_at,
          })),
        )
      }
    } finally {
      setHistoryLoading(false)
    }
  }

  useEffect(() => {
    loadHistory()
  }, [])

  const loadAuditFromHistory = async (item: AuditHistoryItem) => {
    try {
      setLoading(true)
      setError(null)

      // Ensure user is authenticated and API key is valid (re-uses existing helper)
      await getApiKey()

      const { data, error } = await supabase
        .from('site_audits')
        .select('id, url, requested_url, score, issues, metrics, performance, created_at')
        .eq('id', item.id)
        .single()

      if (error || !data) {
        console.error('Error loading audit from history', error)
        setError('Unable to load this audit. Please try again.')
        return
      }

      const metrics = (data as any).metrics || {}
      const categories = (metrics as any).categories || {}
      const highlights = (metrics as any).highlights || []
      const recommendations = (metrics as any).recommendations || []

      const perf = (data as any).performance || {}

      const loaded: SeoAuditResult = {
        audit_id: data.id,
        url: data.url,
        requested_url: data.requested_url ?? data.url,
        overall_score: data.score,
        categories,
        issues: (data as any).issues || [],
        highlights,
        recommendations,
        metrics,
        performance: {
          load_time_ms: perf.load_time_ms ?? null,
          dom_content_loaded_ms: perf.dom_content_loaded_ms ?? null,
          time_to_first_byte_ms: perf.time_to_first_byte_ms ?? null,
        },
        screenshot_base64: null,
        created_at: data.created_at,
      }

      // Reflect the loaded URL in the input
      setUrl(loaded.requested_url || loaded.url)
      setResult(loaded)
    } catch (err: any) {
      console.error('Unexpected error loading audit from history', err)
      setError(err.message || 'Unexpected error while loading this audit')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setResult(null)

    const trimmedUrl = url.trim()
    if (!trimmedUrl) {
      setError('Please enter a URL to audit')
      return
    }

    try {
      setLoading(true)
      const apiKey = await getApiKey()

      const apiBaseUrl = import.meta.env.VITE_SEO_API_BASE_URL || 'https://api.gistmag.co.uk'

      const response = await fetch(`${apiBaseUrl}/seo/audit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          api_key: apiKey,
          url: trimmedUrl,
          include_screenshot: includeScreenshot,
          // Use a valid source enum that the credit system accepts
          source: 'web',
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        setError(errorData.message || errorData.error || `SEO audit failed (${response.status})`)
        return
      }

      const data = await response.json()
      setResult(data)
      // Refresh history so the latest audit appears straight away
      loadHistory()
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred while running the SEO audit')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
      <Sidebar />
      <div className="lg:pl-64">
        <DashboardHeader title="Site Audit" />

        <main className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="mb-8 animate-fade-in">
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                Site Quality Audit 🔍
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Check how healthy a page is across{' '}
                <span className="font-semibold">performance, accessibility, best practices, and SEO</span>. Each audit
                costs <span className="font-semibold text-primary-600 dark:text-primary-400">1 credit</span>.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="glass-card rounded-xl p-6 mb-8 space-y-6 animate-fade-in">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  URL to audit
                </label>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://www.example.com"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Enter any public URL and we&apos;ll run a quick site check with scores and an optional snapshot.
                </p>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={includeScreenshot}
                    onChange={(e) => setIncludeScreenshot(e.target.checked)}
                    className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Capture full-page screenshot
                  </span>
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  1 credit per analysis. Screenshots may slightly increase processing time.
                </p>
              </div>

              {error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
              )}

              <div className="flex items-center space-x-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center px-6 py-3 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                      Running audit...
                    </>
                  ) : (
                    <>
                      <span className="mr-2">Run Site Audit</span>
                      <span>→</span>
                    </>
                  )}
                </button>
              </div>
            </form>

            {/* Results */}
            {result && (
              <div className="space-y-6 animate-fade-in" style={{ animationDelay: '0.1s' }}>
                {/* Score & Summary */}
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="glass-card rounded-xl p-6 flex flex-col items-start justify-between">
                    <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                      Overall Score
                    </h2>
                    <div className="relative w-28 h-28 mb-4">
                      <div
                        className="absolute inset-0 rounded-full"
                        style={{
                          background: `conic-gradient(#4ade80 ${result.overall_score * 3.6}deg, rgba(148,163,184,0.4) 0deg)`,
                        }}
                      />
                      <div className="absolute inset-2 rounded-full bg-white dark:bg-slate-900 flex items-center justify-center">
                        <span className="text-3xl font-bold text-primary-600 dark:text-primary-400">
                          {result.overall_score}
                        </span>
                      </div>
                    </div>
                    <p className="mt-1 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      / 100 overall
                    </p>
                    <p className="mt-3 text-sm text-gray-600 dark:text-gray-400 break-all">
                      {result.url}
                    </p>
                  </div>

                  <div className="glass-card rounded-xl p-6 md:col-span-2">
                    <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                      Category scores
                    </h2>
                    <div className="space-y-3">
                      {([
                        { key: 'performance', label: 'Performance', color: 'bg-emerald-400' },
                        { key: 'accessibility', label: 'Accessibility', color: 'bg-sky-400' },
                        { key: 'best_practices', label: 'Best Practices', color: 'bg-violet-400' },
                        { key: 'seo', label: 'SEO', color: 'bg-amber-400' },
                      ] as const).map((cat) => {
                        const category = result.categories[cat.key]
                        const score = category?.score ?? 0
                        return (
                          <div key={cat.key}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                {cat.label}
                              </span>
                              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                {score}
                                <span className="text-xs text-gray-500 dark:text-gray-400"> / 100</span>
                              </span>
                            </div>
                            <div className="w-full h-2 rounded-full bg-gray-200 dark:bg-slate-800 overflow-hidden">
                              <div
                                className={`h-2 rounded-full ${cat.color}`}
                                style={{ width: `${Math.max(0, Math.min(100, score))}%` }}
                              />
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>

                {/* Metrics, performance & explanations */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="glass-card rounded-xl p-6">
                    <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-4">
                      On-page SEO Metrics
                    </h2>
                    <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
                      <div>
                        <span className="font-semibold">Title:</span>{' '}
                        <span>{result.metrics?.title?.value || '—'}</span>
                        {result.metrics?.title && (
                          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                            ({result.metrics.title.length} chars, {result.metrics.title.status})
                          </span>
                        )}
                      </div>
                      <div>
                        <span className="font-semibold">Meta description:</span>{' '}
                        <span>{result.metrics?.meta_description?.value || '—'}</span>
                      </div>
                      <div>
                        <span className="font-semibold">H1 count:</span>{' '}
                        <span>{result.metrics?.h1_count}</span>
                      </div>
                      <div>
                        <span className="font-semibold">Word count:</span>{' '}
                        <span>{result.metrics?.word_count}</span>
                      </div>
                      <div>
                        <span className="font-semibold">Internal links:</span>{' '}
                        <span>{result.metrics?.internal_links}</span>
                      </div>
                      <div>
                        <span className="font-semibold">External links:</span>{' '}
                        <span>{result.metrics?.external_links}</span>
                      </div>
                      <div>
                        <span className="font-semibold">Images without alt:</span>{' '}
                        <span>{result.metrics?.images_without_alt}</span>
                      </div>
                      <div>
                        <span className="font-semibold">Indexable:</span>{' '}
                        <span>{result.metrics?.indexable ? 'Yes' : 'No'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="glass-card rounded-xl p-6">
                    <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-4">
                      Performance (approximate)
                    </h2>
                    <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
                      <div>
                        <span className="font-semibold">Load time:</span>{' '}
                        <span>
                          {result.performance?.load_time_ms != null
                            ? `${result.performance.load_time_ms} ms`
                            : '—'}
                        </span>
                      </div>
                      <div>
                        <span className="font-semibold">DOM Content Loaded:</span>{' '}
                        <span>
                          {result.performance?.dom_content_loaded_ms != null
                            ? `${result.performance.dom_content_loaded_ms} ms`
                            : '—'}
                        </span>
                      </div>
                      <div>
                        <span className="font-semibold">Time to First Byte:</span>{' '}
                        <span>
                          {result.performance?.time_to_first_byte_ms != null
                            ? `${result.performance.time_to_first_byte_ms} ms`
                            : '—'}
                        </span>
                      </div>
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        These values are approximations from a single headless Chromium run and may differ from
                        real user metrics and Google&apos;s Core Web Vitals.
                      </p>
                    </div>
                  </div>
                </div>

                {/* What went well / What to improve */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="glass-card rounded-xl p-6">
                    <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                      What you&apos;re doing well
                    </h2>
                    {(['performance', 'accessibility', 'best_practices', 'seo'] as const).some(
                      (key) => result.categories[key]?.positives && result.categories[key].positives!.length > 0,
                    ) ? (
                      <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
                        {([
                          { key: 'performance', label: 'Performance' },
                          { key: 'accessibility', label: 'Accessibility' },
                          { key: 'best_practices', label: 'Best practices' },
                          { key: 'seo', label: 'SEO' },
                        ] as const).map((cat) => {
                          const positives = result.categories[cat.key]?.positives || []
                          if (!positives.length) return null
                          return (
                            <div key={cat.key}>
                              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                                {cat.label}
                              </p>
                              <ul className="list-disc list-inside space-y-1">
                                {positives.map((item, idx) => (
                                  <li key={idx}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        We&apos;ll highlight strengths here once there is enough data.
                      </p>
                    )}
                  </div>

                  <div className="glass-card rounded-xl p-6">
                    <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                      Suggested next steps
                    </h2>
                    {result.recommendations && result.recommendations.length > 0 && (
                      <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300 mb-3">
                        {result.recommendations.map((rec, idx) => (
                          <li key={idx}>{rec}</li>
                        ))}
                      </ul>
                    )}

                    <div className="space-y-2">
                      {([
                        { key: 'performance', label: 'Performance' },
                        { key: 'accessibility', label: 'Accessibility' },
                        { key: 'best_practices', label: 'Best practices' },
                        { key: 'seo', label: 'SEO' },
                      ] as const).map((cat) => {
                        const category = result.categories[cat.key]
                        if (!category?.issues?.length) return null
                        return (
                          <div key={cat.key}>
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                              {cat.label}
                            </p>
                            <ul className="list-disc list-inside space-y-1 text-xs text-gray-700 dark:text-gray-300 max-h-32 overflow-y-auto">
                              {category.issues.map((issue, idx) => (
                                <li key={idx}>{issue}</li>
                              ))}
                            </ul>
                          </div>
                        )
                      })}
                    </div>

                    {(!result.recommendations || result.recommendations.length === 0) &&
                      Object.values(result.categories).every((c) => !c?.issues?.length) && (
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                          No major issues detected. This page is in good shape.
                        </p>
                      )}
                  </div>
                </div>

                {/* Screenshot */}
                {result.screenshot_base64 && (
                  <div className="glass-card rounded-xl p-6">
                    <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-4">
                      Page Snapshot
                    </h2>
                    <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-slate-700">
                      <img
                        src={`data:image/png;base64,${result.screenshot_base64}`}
                        alt="SEO audit snapshot"
                        className="w-full h-auto"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Audit history */}
            <div className="mt-10 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400">Recent audits</h2>
                {historyLoading && (
                  <span className="text-xs text-gray-400 dark:text-gray-500">Loading…</span>
                )}
              </div>
              {historyError && (
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                  <p className="text-xs text-amber-800 dark:text-amber-200">{historyError}</p>
                </div>
              )}
              {history.length === 0 && !historyLoading && !historyError && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  You have not run any site audits yet. Your last few results will appear here.
                </p>
              )}
              {history.length > 0 && (
                <div className="space-y-2">
                  {history.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between px-4 py-3 rounded-lg border border-gray-200 dark:border-slate-800 bg-white/60 dark:bg-slate-900/60"
                    >
                      <div className="min-w-0 mr-4">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {item.url}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(item.created_at).toLocaleString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm font-semibold text-primary-600 dark:text-primary-400">
                            {item.score}
                            <span className="text-xs text-gray-500 dark:text-gray-400"> / 100</span>
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Overall score</p>
                        </div>
                        <div>
                          <button
                            type="button"
                            onClick={() => loadAuditFromHistory(item)}
                            className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-300 dark:border-slate-700 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-900 hover:bg-gray-100 dark:hover:bg-slate-800 transition"
                          >
                            View full audit
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}

